import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { listPackages, createCheckout } from "@/lib/api";
import { CheckCircle, Lightning, Sparkle } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import Footer from "@/components/Footer";

const PACK_PERKS = {
  starter: [
    "5 Irresistible Offer generations",
    "Full PDF export & sharing",
    "Refine any saved offer",
  ],
  pro: [
    "20 Irresistible Offer generations",
    "Everything in Starter",
    "Side-by-side comparison",
    "Save unlimited drafts",
  ],
  agency: [
    "50 Irresistible Offer generations",
    "Everything in Pro",
    "Priority generation",
    "Best for client work",
  ],
};

const HIGHLIGHT = "pro";

export default function Pricing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    listPackages().then(setPacks).catch(() => toast.error("Could not load packs"));
  }, []);

  const handleBuy = async (packId) => {
    if (!user) {
      navigate("/login");
      return;
    }
    setBusyId(packId);
    try {
      const { url } = await createCheckout(packId, window.location.origin);
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8] flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" />
      <div className="flex-1 max-w-6xl mx-auto w-full px-6 md:px-10 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-4">
            Credit Packs
          </p>
          <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter leading-none">
            Pay only for{" "}
            <span className="font-black">offers you forge</span>.
          </h1>
          <p className="mt-6 text-[#9AA5B5] max-w-2xl mx-auto font-body">
            Your first offer is on us. After that, top up with a credit pack —
            no subscriptions, credits never expire.
          </p>
          {user && (
            <p
              className="mt-4 text-sm text-[#C9A961] font-mono"
              data-testid="pricing-current-credits"
            >
              You have <span className="font-bold">{user.credits}</span> credit
              {user.credits === 1 ? "" : "s"} remaining
              {!user.has_used_free_credit && " + 1 free generation"}
            </p>
          )}
        </div>

        <div className="grid grid-cols-12 gap-6">
          {packs.map((pack, i) => {
            const isHi = pack.id === HIGHLIGHT;
            return (
              <motion.div
                key={pack.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`col-span-12 md:col-span-4 relative rounded-lg p-8 md:p-10 flex flex-col ${
                  isHi
                    ? "bg-gradient-to-b from-[#0F1F38] to-[#0F1F38] border border-[#C9A961]/40 glow-amber"
                    : "bg-[#0F1F38] border border-white/5"
                }`}
                data-testid={`pack-card-${pack.id}`}
              >
                {isHi && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A961] text-[#0A1628] text-[10px] tracking-[0.25em] uppercase font-black px-3 py-1 rounded-sm">
                    Most popular
                  </div>
                )}
                <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961] mb-2">
                  {pack.name}
                </p>
                <div className="flex items-baseline gap-2 mb-3">
                  <div className="font-display font-black text-5xl text-white">
                    A${(pack.price_aud ?? pack.price_usd).toFixed(0)}
                  </div>
                  <div className="text-sm text-[#9AA5B5]">one-time</div>
                </div>
                <div className="text-sm text-[#C9A961] mb-6 font-medium">
                  {pack.credits} offers · A${((pack.price_aud ?? pack.price_usd) / pack.credits).toFixed(2)} each
                </div>

                <ul className="space-y-3 flex-1 mb-8">
                  {PACK_PERKS[pack.id]?.map((perk) => (
                    <li
                      key={perk}
                      className="flex gap-2 text-sm text-[#F5F1E8]"
                    >
                      <CheckCircle
                        weight="fill"
                        className="h-4 w-4 text-[#C9A961] mt-0.5 shrink-0"
                      />
                      {perk}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleBuy(pack.id)}
                  disabled={busyId === pack.id}
                  className={`rounded-sm py-5 font-bold tracking-wide ${
                    isHi
                      ? "bg-[#C9A961] text-[#0A1628] hover:bg-[#E0B64B]"
                      : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                  }`}
                  data-testid={`pack-buy-btn-${pack.id}`}
                >
                  {busyId === pack.id ? (
                    <>
                      <div className="mr-2 h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                      Redirecting…
                    </>
                  ) : (
                    <>
                      <Lightning weight="fill" className="mr-2 h-4 w-4" />
                      Buy {pack.credits} credits
                    </>
                  )}
                </Button>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-16 text-center text-sm text-[#808A9B]">
          <Sparkle className="inline h-4 w-4 text-[#C9A961] mr-1" />
          Secure checkout powered by Stripe. Credits never expire.
        </div>
      </div>
      <Footer />
    </div>
  );
}
